import * as React from "react";
import { useState, useCallback, useRef, useEffect } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { FormFields } from "../types/StoreMap";
import Grid from "@mui/material/Grid";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { createFilterOptions, FilterOptionsState } from "@mui/material";
import { Suggestion } from "../utils/placesAutocomplete";

const DEBOUNCE_MS = 300;

interface LocationSearchInputProps {
  suggestion: string | Suggestion | null;
  setSuggestion: React.Dispatch<
    React.SetStateAction<string | Suggestion | null>
  >;
  value: string;
  handleChange: (value: string) => void;
  error?: boolean;
  helperText?: string | false;
  disabled: boolean;
  required: boolean;
  handleSelect: (
    address: string,
    placeId?: string,
    suggestion?: Suggestion
  ) => Promise<void>;
  countrySelected: string;
}

const LocationSearchInput = ({
  suggestion,
  setSuggestion,
  value,
  handleChange,
  error,
  helperText,
  disabled,
  required,
  handleSelect,
  countrySelected,
}: LocationSearchInputProps) => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(() => {
    if (window.google?.maps?.places) {
      sessionToken.current =
        new google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input || input.length < 2) {
        setSuggestions([]);
        return;
      }
      if (!window.google?.maps?.places?.AutocompleteSuggestion) {
        console.error(
          "google.maps.places.AutocompleteSuggestion is not available"
        );
        return;
      }
      setLoading(true);
      try {
        const { suggestions: results } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input,
              includedRegionCodes: [countrySelected.toLowerCase() || "us"],
              sessionToken: sessionToken.current ?? undefined,
            }
          );
        const mapped = results
          .filter((s) => s.placePrediction !== null)
          .map((s, idx) => {
            const pred = s.placePrediction!;
            const matches = pred.text.matches ?? [];
            return {
              id: pred.placeId,
              description: pred.text.text,
              placeId: pred.placeId,
              active: idx === 0,
              index: idx,
              formattedSuggestion: {
                mainText: pred.mainText?.text ?? pred.text.text,
                secondaryText: pred.secondaryText?.text ?? "",
              },
              matchedSubstrings: matches.map((m) => ({
                offset: m.startOffset ?? 0,
                length: (m.endOffset ?? 0) - (m.startOffset ?? 0),
              })),
              terms: [],
              types: pred.types ?? [],
            } as Suggestion;
          });
        setSuggestions(mapped);
      } catch (err) {
        console.error("AutocompleteSuggestion error:", err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [countrySelected]
  );

  const onInputChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      handleChange(newValue);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(
        () => fetchSuggestions(newValue),
        DEBOUNCE_MS
      );
    },
    [handleChange, fetchSuggestions]
  );

  const onChange = useCallback(
    async (
      _event: React.SyntheticEvent,
      newValue: string | Suggestion | null
    ) => {
      setSuggestion(newValue);
      if (newValue && typeof newValue !== "string") {
        await handleSelect(newValue.description, newValue.placeId, newValue);
        if (window.google?.maps?.places) {
          sessionToken.current =
            new google.maps.places.AutocompleteSessionToken();
        }
      }
    },
    [setSuggestion, handleSelect]
  );

  const filter = createFilterOptions<Suggestion>();

  return (
    <Autocomplete
      id="google-map"
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : option.formattedSuggestion.mainText
      }
      options={suggestions}
      autoComplete
      includeInputInList
      value={suggestion}
      onChange={onChange}
      inputValue={value}
      onInputChange={onInputChange}
      filterOptions={(options, params: FilterOptionsState<Suggestion>) => {
        const filtered = filter(options, params);
        const { inputValue } = params;
        const isExisting = options.some(
          (option) => inputValue === option.description
        );
        if (inputValue !== "" && !isExisting) {
          filtered.push({
            id: "add",
            active: true,
            index: 0,
            description: inputValue,
            placeId: "add",
            formattedSuggestion: {
              mainText: inputValue,
              secondaryText: inputValue,
            },
            matchedSubstrings: [],
            terms: [],
            types: [],
          });
        }
        return filtered;
      }}
      isOptionEqualToValue={(option, value) =>
        option.placeId === value.placeId
      }
      freeSolo
      disabled={disabled}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          name={FormFields.Address}
          id={FormFields.Address}
          fullWidth
          size="small"
          error={error}
          required={required}
          helperText={helperText}
          label={t("form.address")}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.placeId}>
            <Grid container alignItems="center">
              <Grid item>
                <Box
                  component={LocationOnIcon}
                  sx={{ color: option.active ? "red" : "inherit" }}
                />
              </Grid>
              <Grid item xs>
                <span>{option.description}</span>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default LocationSearchInput;
